/**
 * «Посевной план» варианта — распределение культур ПО ПОЛЯМ.
 *
 * Заказ founder (19.08): «мне нужен посевной план, мне надо понять, на каком
 * поле какую культуру посажу в плановом году, и нужны варианты таких посевных
 * планов». Экран отвечает ровно на этот вопрос.
 *
 * Макет: `docs/superpowers/prototypes/annual-planning/v2-15-sowing-plan.html`
 * (утверждён founder 19.08). Структура оттуда: тулбар фильтров сверху, полоса
 * массового назначения при выборе, таблица слева + СПП-карточка справа.
 *
 * Как экран встаёт между соседями по разделу:
 *   • ЗДЕСЬ агроном решает «что где сеем» — построчно по полям;
 *   • «Структура посевных площадей» — тот же вариант, свёрнутый по культурам;
 *   • «Вариант плана» — тот же вариант глазами технолога: на строку «культура
 *     по предшественнику» вешается техкарта и появляются деньги.
 * Источник у всех трёх один — посевы варианта, поэтому цифры обязаны сходиться.
 *
 * Чего в этой вехе СОЗНАТЕЛЬНО нет:
 *   • автозаполнения по правилам и по севообороту — это этап 2 (отдельный PR).
 *     Кнопок-заглушек «этап 2» на экране нет вовсе: мёртвая кнопка обещает то,
 *     чего сегодня не будет, и один раз нажатая — подрывает доверие ко всем
 *     остальным;
 *   • раскраски полей на карте и перетаскивания — таблица закрывает вопрос
 *     целиком, карта была бы вторым местом, где потом чинить расхождения.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { TileGrid } from '@/components/ui/tile-grid';
import { describeApiError } from '@/lib/apiError';
import { useCan } from '@/lib/auth/usePermissions';
import {
  useClearFieldsInVariant,
  usePatchFieldSowing,
  useVariantFields,
  type ClearTarget,
  type VariantField,
} from '@/lib/planning/planCrops';
import { formatHa } from '@/lib/planning/planVariant';
import { usePlanVariants } from '@/lib/planning/techCards';
import { useSeasonByYear } from '@/lib/planning/useSeasonByYear';
import { CreateVariantButton } from '../CreateVariantButton';
import { FarmScopeNotice } from '@/components/scope/FarmScopeNotice';
import { usePlanningFarmScope } from '@/components/scope/usePlanningFarmScope';
import { SeasonNotStartedCard, SeasonYearOutOfWindowCard } from '../SeasonBudgetStates';
import { VariantTabs } from '../plan-variant/VariantTabs';
import {
  cropsWithoutDefault,
  emptyYieldRows,
  listCropNames,
  yieldGapsByCrop,
} from '@/lib/planning/applyYieldDefaults';
import { ApplyYieldDefaultsButton } from './ApplyYieldDefaultsButton';
import { AssignCropDialog } from './AssignCropDialog';
import { AutoFillPanel } from './AutoFillPanel';
import { FieldRowsTable } from './FieldRowsTable';
import { HistoryEntryDialog } from './HistoryEntryDialog';
import { RotationRulesPanel } from './RotationRulesPanel';
import { SppCard } from './SppCard';
import {
  filterSowingPlan,
  hasActiveFilter,
  historyOptions,
  isRowEditable,
  planOptions,
  sowingPlanTotals,
  totalAreaOfIds,
  PLAN_NONE,
} from './sowingPlan';

interface SowingPlanSearch {
  farm?: string;
  variant?: string;
  prev?: string;
  prev2?: string;
  plan?: string;
}

function Tile({ value, label, tone }: { value: string; label: string; tone?: 'warn' }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div
          className={
            tone === 'warn'
              ? 'text-2xl font-semibold text-accent-brand'
              : 'text-2xl font-semibold text-foreground'
          }
        >
          {value}
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

/** Radix Select не принимает пустую строку как значение — нужен свой маркер. */
const ANY_VALUE = '__any__';

/** Выпадашка фильтра: «любой» + то, что реально встречается в хозяйстве. */
function FilterSelect({
  label,
  anyLabel,
  options,
  value,
  onChange,
}: {
  label: string;
  anyLabel: string;
  options: { id: string; name: string }[];
  value: string | undefined;
  onChange: (next: string | undefined) => void;
}) {
  return (
    <Select
      value={value ?? ANY_VALUE}
      onValueChange={(v) => onChange(v === ANY_VALUE ? undefined : v)}
    >
      <SelectTrigger className="w-auto min-w-[200px]" aria-label={label}>
        <SelectValue placeholder={anyLabel} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY_VALUE}>{anyLabel}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.id} value={o.id}>
            {o.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function SowingPlanPage() {
  const params = useParams({ strict: false }) as { year?: string };
  const year = Number(params.year);
  const search = useSearch({ strict: false }) as SowingPlanSearch;
  const navigate = useNavigate();
  const season = useSeasonByYear(year);

  // Пашню варианта отдаёт та же ручка, что кормит заведение культур, и она под
  // правом на техкарты — производный экран наследует права источника.
  const canRead = useCan('tech_cards', 'r');
  const canSow = useCan('sowings', 'c');
  const canRemove = useCan('sowings', 'd');
  const canUpdate = useCan('sowings', 'u');

  // Выделение строк объявлено до хозяйства: смена области его сбрасывает.
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Хозяйство — из переключателя слева (AK-866 В-3: область — из
  // переключателя); `?farm=` — только присланная ссылка (AK-897). «Первое по
  // алфавиту» так и не подставляем — это решение за человека, принятое молча.
  const farmScope = usePlanningFarmScope({
    searchFarm: search.farm,
    onScopeChanged: () => {
      setSelected(new Set());
      void navigate({
        to: '/planning/seasons/$year/sowing-plan',
        params: { year: String(year) },
        search: {
          ...search,
          farm: undefined,
          variant: undefined,
          prev: undefined,
          prev2: undefined,
          plan: undefined,
        },
        replace: true,
      });
    },
    onDeepLinkApplied: () =>
      void navigate({
        to: '/planning/seasons/$year/sowing-plan',
        params: { year: String(year) },
        search: { ...search, farm: undefined },
        replace: true,
      }),
  });
  const { farmId } = farmScope;
  const farmName = farmScope.farm?.name ?? null;

  const variants = usePlanVariants(canRead ? farmId : null, season.seasonId);
  // По умолчанию открываем «план сезона» (is_selected) — именно его защищают.
  const variant =
    (variants.data ?? []).find((v) => v.id === search.variant) ??
    (variants.data ?? []).find((v) => v.is_selected) ??
    (variants.data ?? [])[0] ??
    null;

  const fields = useVariantFields(canRead ? (variant?.id ?? null) : null);
  const clear = useClearFieldsInVariant();
  const patch = usePatchFieldSowing();

  /** Поле → цель снятия: имя и га нужны итогу, культура — тексту про одно поле. */
  const asClearTarget = (f: VariantField): ClearTarget => ({
    id: f.id,
    name: f.name,
    area_ha: f.area_ha,
    cropName: f.plan_crop_name ?? f.occupied_by,
  });

  const [query, setQuery] = useState('');
  const [assigning, setAssigning] = useState<VariantField[] | null>(null);
  // Ручной ввод истории (AK-694): клик по пустой ячейке предшественника.
  // Колонка задаёт сезон — храним её вместе с полем, а не вычисляем на сабмите.
  const [historyEntry, setHistoryEntry] = useState<{
    field: VariantField;
    which: 'predecessor' | 'predecessor2';
  } | null>(null);

  const allFields = useMemo(() => fields.data ?? [], [fields.data]);
  const filter = useMemo(
    () => ({
      query,
      predecessor: search.prev,
      predecessor2: search.prev2,
      plan: search.plan,
    }),
    [query, search.prev, search.prev2, search.plan],
  );
  const shown = useMemo(() => filterSowingPlan(allFields, filter), [allFields, filter]);
  // Строк без урожайности во ВСЁМ варианте (не под фильтром): подпись рядом с
  // кнопкой обязана говорить про то же множество, что и сама кнопка.
  const emptyYieldTotal = useMemo(
    () => emptyYieldRows(yieldGapsByCrop(allFields)),
    [allFields],
  );
  // Культуры, которым норматив не заведён вовсе: их строки кнопка не заполнит,
  // и человеку нужен адрес, куда идти заводить (AK-789).
  const noNormCrops = useMemo(
    () => cropsWithoutDefault(yieldGapsByCrop(allFields)),
    [allFields],
  );
  const totals = useMemo(() => sowingPlanTotals(allFields), [allFields]);
  const selectedFields = allFields.filter((f) => selected.has(f.id));
  const selectedArea = totalAreaOfIds(allFields, selected);
  // Снимать есть что только с занятых полей, и зоны отсюда не трогаем.
  // Считаем заранее: кнопка называет число, а не молча ничего не делает.
  const clearable = selectedFields.filter((f) => !!f.occupied_by && !f.occupied_by_subzones);

  const prevOptions = useMemo(() => historyOptions(allFields, 'predecessor'), [allFields]);
  const prev2Options = useMemo(() => historyOptions(allFields, 'predecessor2'), [allFields]);
  const cropOptions = useMemo(
    () => [{ id: PLAN_NONE, name: '— не назначена —' }, ...planOptions(allFields)],
    [allFields],
  );
  const prevYear = allFields.find((f) => f.predecessor_year !== null)?.predecessor_year ?? null;

  const setSearch = (next: SowingPlanSearch) =>
    void navigate({
      to: '/planning/seasons/$year/sowing-plan',
      params: { year: String(year) },
      search: { ...search, ...next },
    });

  // Пока идёт массовое снятие, «занятыми» считаются ВСЕ поля партии: строка,
  // до которой очередь ещё не дошла, всё равно вот-вот изменится, и давать по
  // ней править — значит обещать правку, которую тут же перетрут.
  const pendingIds = useMemo(() => {
    if (patch.isPending && patch.variables?.fieldId) return new Set([patch.variables.fieldId]);
    if (clear.isPending) return new Set((clear.variables?.fields ?? []).map((f) => f.id));
    return new Set<string>();
  }, [patch.isPending, patch.variables, clear.isPending, clear.variables]);

  const seasonResolved = !season.isLoading && !season.isError;

  return (
    // AK-866 В-3: область — из переключателя. Своего выбора хозяйства на
    // экране нет; `POST /scope/set` отсюда зовётся только deep-link'ом
    // `?farm=` и только через `useScopeSetter` (AK-897).
    <AppShell>
      <div className="mx-auto w-full max-w-[1600px] pb-safe-fab">
        <PageHeader
          title={season.validYear ? `Посевной план · сезон ${year}` : 'Посевной план'}
          showBack
          breadcrumbs={[
            { label: 'Планирование сезона', to: '/planning' },
            { label: season.validYear ? `Бюджет сезона ${year}` : 'Бюджет сезона' },
            { label: 'Посевной план' },
          ]}
          description="На каком поле какую культуру сеем в плановом году. Вариантов может быть несколько — это прикидка, факт хозяйства она не трогает."
        />

        <Stack gap={4} className="mt-4">
          {!season.validYear && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Год в адресе страницы некорректный.
              </CardContent>
            </Card>
          )}

          {season.validYear && season.isLoading && <Skeleton className="h-96 w-full" />}

          {season.validYear && season.isError && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Не удалось загрузить сезоны. Обновите страницу.
              </CardContent>
            </Card>
          )}

          {season.validYear && seasonResolved && !season.seasonId && !season.card && (
            <SeasonYearOutOfWindowCard year={year} />
          )}
          {season.validYear && seasonResolved && !season.seasonId && !!season.card && (
            <SeasonNotStartedCard year={year} onOpened={() => undefined} />
          )}

          {season.seasonId && !canRead && (
            <Card>
              <CardContent className="p-4 text-sm text-muted-foreground">
                Посевной план вам не открыт: он показывает плановую урожайность и
                технологии, а их даёт право на технологические карты. Попросите доступ у
                администратора хозяйства.
              </CardContent>
            </Card>
          )}

          {season.seasonId && canRead && (
            <>
              <Stack gap={2}>
                <FarmScopeNotice scope={farmScope} />
                {(variants.data ?? []).length > 0 && (
                  <VariantTabs
                    variants={variants.data ?? []}
                    currentId={variant?.id ?? null}
                    onSelect={(id) => {
                      setSelected(new Set());
                      setSearch({ variant: id });
                    }}
                    actions={
                      <CreateVariantButton
                        farmId={farmId}
                        seasonId={season.seasonId}
                        seasonYear={year}
                        farmName={farmName}
                        existingNames={(variants.data ?? []).map((v) => v.name)}
                        onCreated={(id) => setSearch({ variant: id })}
                      />
                    }
                  />
                )}
              </Stack>

              {farmId && variants.isLoading && <Skeleton className="h-24 w-full" />}

              {/* Ошибка ≠ пустота: показать на неё «Создать вариант плана» —
                  значит позвать человека в действие, которое тоже упадёт. */}
              {farmId && variants.isError && (
                <Card>
                  <CardContent className="p-4 text-sm text-destructive">
                    {describeApiError(variants.error)}
                  </CardContent>
                </Card>
              )}

              {farmId && !variants.isLoading && !variants.isError && (variants.data ?? []).length === 0 && (
                <Card>
                  <CardContent className="p-4">
                    <Stack gap={2}>
                      <p className="text-sm text-foreground">
                        У этого хозяйства ещё нет ни одного варианта плана на сезон {year}.
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Посевной план ведётся внутри варианта: так их можно держать
                        несколько и сравнивать, не ломая друг друга.
                      </p>
                      <div>
                        <CreateVariantButton
                          emptyState
                          farmId={farmId}
                          seasonId={season.seasonId}
                          seasonYear={year}
                          farmName={farmName}
                          existingNames={[]}
                          onCreated={(id) => setSearch({ variant: id })}
                        />
                      </div>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {variant && (
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={variant.is_selected ? 'primary' : 'outline'}>
                    {variant.is_selected ? 'план сезона' : 'вариант плана'}
                  </Badge>
                  <span className="text-sm text-muted-foreground">{variant.name}</span>
                  <Button asChild variant="outline" size="sm" className="ml-auto">
                    <Link
                      to="/planning/seasons/$year/crop-structure"
                      params={{ year: String(year) }}
                      search={{ variant: variant.id }}
                    >
                      Свод по культурам
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <Link
                      to="/planning/seasons/$year/plan-variant"
                      params={{ year: String(year) }}
                      search={{ variant: variant.id }}
                    >
                      Технологии и деньги
                    </Link>
                  </Button>
                </div>
              )}

              {fields.isLoading && variant && <Skeleton className="h-96 w-full" />}

              {fields.isError && (
                <Card>
                  <CardContent className="p-4 text-sm text-destructive">
                    {describeApiError(fields.error)}
                  </CardContent>
                </Card>
              )}

              {variant && fields.data && (
                <>
                  <TileGrid cols={4}>
                    <Tile value={String(totals.fieldsTotal)} label="полей" />
                    <Tile value={formatHa(totals.arableHa)} label="пашня хозяйства" />
                    <Tile
                      value={`${totals.assignedFields} / ${totals.fieldsTotal}`}
                      label="полей с плановой культурой"
                    />
                    <Tile
                      value={formatHa(totals.cropsAreaHa)}
                      label={
                        totals.arableHa > 0
                          ? `под культурами · ${Math.round((totals.cropsAreaHa / totals.arableHa) * 100)} %`
                          : 'под культурами'
                      }
                    />
                    <Tile
                      value={formatHa(totals.unassignedHa)}
                      label="культура не назначена"
                      tone={totals.unassignedFields > 0 ? 'warn' : undefined}
                    />
                    <Tile
                      value={prevYear ? String(prevYear) : '—'}
                      label="сезон-факт для предшественников"
                    />
                  </TileGrid>

                  {totals.fieldsTotal === 0 && (
                    <Card>
                      <CardContent className="p-4 text-sm text-muted-foreground">
                        Пашня считается по полям, у которых в карточке задано угодье с
                        севооборотом («Пашня») и которые не выведены из оборота. Сейчас
                        таких полей у хозяйства нет — сеять пока не на что. Проверьте
                        угодье в карточках полей.
                      </CardContent>
                    </Card>
                  )}

                  {totals.fieldsTotal > 0 && (
                    <>
                      <Card>
                        <CardContent className="flex flex-wrap items-center gap-2 p-3">
                          <span className="text-sm text-muted-foreground">Фильтр:</span>
                          <FilterSelect
                            label="Фильтр по предшественнику"
                            anyLabel="Предшественник: любой"
                            options={prevOptions}
                            value={search.prev}
                            onChange={(v) => setSearch({ prev: v })}
                          />
                          <FilterSelect
                            label="Фильтр по препредшественнику"
                            anyLabel="Препредшественник: любой"
                            options={prev2Options}
                            value={search.prev2}
                            onChange={(v) => setSearch({ prev2: v })}
                          />
                          <FilterSelect
                            label="Фильтр по плановой культуре"
                            anyLabel="Плановая: все"
                            options={cropOptions}
                            value={search.plan}
                            onChange={(v) => setSearch({ plan: v })}
                          />
                          <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Поиск поля…"
                            className="h-9 max-w-[200px]"
                            aria-label="Поиск поля по названию"
                          />
                          <span className="ml-auto text-sm text-muted-foreground">
                            показано полей: {shown.length} из {totals.fieldsTotal}
                          </span>
                          {hasActiveFilter(filter) && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setQuery('');
                                setSearch({ prev: undefined, prev2: undefined, plan: undefined });
                              }}
                            >
                              Сбросить фильтр
                            </Button>
                          )}
                          {/* «Подтянуть нормативы урожайности» (AK-778) — рядом
                              с фильтрами, как в макете 08. Культура для выбора
                              «только X» берётся из активного фильтра плановой
                              культуры: агроном отфильтровал таблицу по ней —
                              значит про неё сейчас и думает. */}
                          <div className="flex basis-full flex-wrap items-center gap-2">
                            <ApplyYieldDefaultsButton
                              scenarioId={variant.id}
                              fields={allFields}
                              canUpdate={canUpdate}
                              scopeCropId={
                                search.plan && search.plan !== PLAN_NONE ? search.plan : null
                              }
                            />
                            {canUpdate && (
                              <span className="text-xs text-muted-foreground">
                                {emptyYieldTotal > 0
                                  ? `${emptyYieldTotal} строк плана без урожайности — их завели раньше, чем в хозяйстве появились нормативы по культурам. По таким полям валовка и выручка не считаются.`
                                  : 'Все строки плана уже с урожайностью — подтягивать нечего.'}
                              </span>
                            )}
                            {/* Связка с экраном нормативов (AK-789). Раньше
                                кнопка честно называла культуры без норматива —
                                и вести с ними было НЕКУДА: справочника в
                                интерфейсе не существовало. Ссылка ведёт ровно
                                на нужную культуру. */}
                            {canUpdate && noNormCrops.length > 0 && (
                              <Link
                                to="/planning/seasons/$year/yield-norms"
                                params={{ year: String(year) }}
                                search={{ crop: noNormCrops[0].cropId }}
                                className="text-xs font-medium text-primary underline-offset-2 hover:underline"
                              >
                                Норматива нет: {listCropNames(noNormCrops)} — завести
                                норматив урожайности →
                              </Link>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      {(canSow || canRemove) && selected.size > 0 && (
                        <Card className="border-primary/40 bg-primary/5">
                          <CardContent className="flex flex-wrap items-center gap-3 p-3">
                            <span className="text-sm font-medium text-foreground">
                              Выбрано полей: {selected.size} · {formatHa(selectedArea)}
                            </span>
                            {canSow && (
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => setAssigning(selectedFields)}
                              >
                                Назначить культуру выбранным
                              </Button>
                            )}
                            {canRemove && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={clear.isPending || clearable.length === 0}
                                onClick={() => {
                                  clear.mutate(
                                    {
                                      scenarioId: variant.id,
                                      fields: clearable.map(asClearTarget),
                                    },
                                    { onSettled: () => setSelected(new Set()) },
                                  );
                                }}
                              >
                                {clear.isPending
                                  ? 'Снимаем…'
                                  : `Снять культуру${clearable.length > 0 ? ` (${clearable.length})` : ''}`}
                              </Button>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => setSelected(new Set())}
                            >
                              Снять выбор
                            </Button>
                          </CardContent>
                        </Card>
                      )}

                      {/* Таблица и СПП рядом (макет v2-15): «поменял поле —
                          что стало со структурой» не должно стоить перехода.
                          На узких экранах — в столбик. */}
                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
                        <Card>
                          <CardContent className="p-4">
                            {shown.length === 0 ? (
                              <p className="text-sm text-muted-foreground">
                                {hasActiveFilter(filter)
                                  ? 'Под фильтр не попало ни одного поля. Сбросьте фильтр, чтобы увидеть всю пашню.'
                                  : 'Полей нет.'}
                              </p>
                            ) : (
                              <FieldRowsTable
                                fields={shown}
                                selected={selected}
                                canAssign={canSow}
                                canClear={canRemove}
                                canTune={canUpdate}
                                onToggle={(field) =>
                                  setSelected((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(field.id)) next.delete(field.id);
                                    else next.add(field.id);
                                    return next;
                                  })
                                }
                                onToggleAll={(checked) =>
                                  setSelected(
                                    checked
                                      ? new Set(shown.filter(isRowEditable).map((f) => f.id))
                                      : new Set(),
                                  )
                                }
                                onAssign={(field) => setAssigning([field])}
                                onFillHistory={(field, which) =>
                                  setHistoryEntry({ field, which })
                                }
                                onClear={(field) =>
                                  clear.mutate({
                                    scenarioId: variant.id,
                                    fields: [asClearTarget(field)],
                                  })
                                }
                                onPickVariety={(field, varietyId) =>
                                  patch.mutate({
                                    scenarioId: variant.id,
                                    fieldId: field.id,
                                    fieldName: field.name,
                                    setVariety: true,
                                    varietyId,
                                  })
                                }
                                onCommitYield={(field, value) =>
                                  patch.mutate({
                                    scenarioId: variant.id,
                                    fieldId: field.id,
                                    fieldName: field.name,
                                    setYield: true,
                                    plannedYieldCHa: value,
                                  })
                                }
                                pendingIds={pendingIds}
                              />
                            )}
                            <p className="mt-3 text-xs text-muted-foreground">
                              Поле, засеянное по зонам, правится в карточке поля — строка
                              подсвечена и показывает причину. Смена культуры пересобирает
                              структуру посевных площадей и назначение техкарт.
                            </p>
                          </CardContent>
                        </Card>

                        <div className="space-y-4">
                          {/* Правила — над сводкой: сначала инструмент, которым
                              структуру МЕНЯЮТ, потом то, что из неё получилось.
                              Обратный порядок заставлял бы агронома листать
                              вниз после каждого применения, чтобы увидеть
                              результат нажатия. */}
                          <RotationRulesPanel
                            scenarioId={variant.id}
                            farmId={farmId}
                            canSow={canSow}
                          />
                          <SppCard
                            fields={allFields}
                            variantName={variant.name}
                            // Вариант Б макета 08: то же действие в строке
                            // культуры — там сразу видно, у КАКОЙ культуры
                            // сколько пустых строк и есть ли у неё норматив.
                            cropAction={(cropId) => (
                              <ApplyYieldDefaultsButton
                                scenarioId={variant.id}
                                fields={allFields}
                                canUpdate={canUpdate}
                                scopeCropId={cropId}
                                tone="row"
                              />
                            )}
                          />
                        </div>
                      </div>

                      {/* Автозаполнение — под таблицей, как в макете v2-15:
                          сначала человек видит, что уже разложено, и только
                          потом ему предлагают доложить остальное скопом. */}
                      {(totals.unassignedFields > 0 ||
                        allFields.some((f) => f.plan_row_origin === 'auto')) && (
                        <AutoFillPanel
                          scenarioId={variant.id}
                          fields={allFields}
                          canSow={canSow}
                          canRemove={canRemove}
                          variantStatus={variant.status}
                        />
                      )}

                      {!canSow && !canUpdate && (
                        <p className="text-sm text-muted-foreground">
                          Менять посевной план может пользователь с правом на посевы —
                          обратитесь к агроному или администратору. Смотреть план это не
                          мешает.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Stack>
      </div>

      {historyEntry &&
        variant &&
        (() => {
          const seasonYear =
            historyEntry.which === 'predecessor'
              ? historyEntry.field.predecessor_year
              : historyEntry.field.predecessor2_year;
          if (seasonYear === null) return null; // ячейка без года некликабельна
          return (
            <HistoryEntryDialog
              // Смена ячейки = новая форма: без key React переиспользовал бы
              // смонтированный диалог, и площадь/культура прежнего поля залипли
              // бы в черновиках.
              key={`${historyEntry.field.id}:${historyEntry.which}`}
              scenarioId={variant.id}
              field={historyEntry.field}
              seasonYear={seasonYear}
              // Кандидаты массового применения — выбранные строки, кроме самой
              // ячейки: «применить также к выбранным» не должно дублировать её.
              selectedOthers={selectedFields.filter((f) => f.id !== historyEntry.field.id)}
              open
              onClose={() => setHistoryEntry(null)}
            />
          );
        })()}

      {assigning && variant && (
        <AssignCropDialog
          scenarioId={variant.id}
          seasonYear={year}
          fields={assigning}
          open
          // Выбор строк переживает отмену диалога: человек отбирал поля
          // фильтром, «передумал с культурой» ≠ «передумал с полями».
          // Чистим его только когда культура действительно назначена.
          onClose={() => setAssigning(null)}
          onAssigned={() => setSelected(new Set())}
        />
      )}
    </AppShell>
  );
}

export default SowingPlanPage;
